import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import ToolPageTemplate from '../templates/ToolPageTemplate'
import Input from '../atoms/Input'
import Button from '../atoms/Button'
import ResultBox from '../organisms/ResultBox'
import { setDateOfBirth, calculateAge, clearAgeResult } from '../store/slices/ageSlice'
import './AgePage.css'

const AgePage = ({ tools = [] }) => {
  const dispatch = useDispatch()
  const { dateOfBirth, result } = useSelector(state => state.age)

  const [lastUpdated, setLastUpdated] = useState(null)
  const [maxDate, setMaxDate] = useState('')          // ← ADD THIS LINE

  const handleDateChange = (e) => {
    dispatch(setDateOfBirth(e.target.value))
  }

  const handleCalculate = () => {
    dispatch(calculateAge())
  }

  const handleClear = () => {
    dispatch(setDateOfBirth(''))
    dispatch(clearAgeResult())
  }

  useEffect(() => {
    if (dateOfBirth) {
      dispatch(calculateAge())
    }
  }, [dateOfBirth, dispatch])

  useEffect(() => {
    setMaxDate(new Date().toISOString().split('T')[0])   // ← ADD THIS BLOCK
  }, [])


  return (
    <ToolPageTemplate path="/age/" tools={tools}>
      <div>
        <section className="age-calculator">
          <div className="age-input-section">
            <Input
              type="date"
              label="Date of Birth"
              value={dateOfBirth}
              onChange={handleDateChange}
              max={maxDate}
            />
            <div className="age-buttons">
              <Button onClick={handleCalculate} disabled={!dateOfBirth}>
                Calculate Age
              </Button>
              <Button variant="secondary" onClick={handleClear}>
                Clear
              </Button>
            </div>
          </div>

          {result && (
            <div className="age-results">
              <ResultBox title="Age Details">
                <div className="age-details">
                  <div className="age-item">
                    <span className="age-label">Years:</span>
                    <span className="age-value">{result.years}</span>
                  </div>
                  <div className="age-item">
                    <span className="age-label">Months:</span>
                    <span className="age-value">{result.months}</span>
                  </div>
                  <div className="age-item">
                    <span className="age-label">Days:</span>
                    <span className="age-value">{result.days}</span>
                  </div>
                  <div className="age-item">
                    <span className="age-label">Total Days:</span>
                    <span className="age-value">{result.totalDays.toLocaleString()}</span>
                  </div>
                  <div className="age-item">
                    <span className="age-label">Total Hours:</span>
                    <span className="age-value">{result.hours.toLocaleString()}</span>
                  </div>
                  <div className="age-item">
                    <span className="age-label">Total Minutes:</span>
                    <span className="age-value">{result.minutes.toLocaleString()}</span>
                  </div>
                </div>
              </ResultBox>

              <ResultBox title="Additional Information">
                <div className="age-additional">
                  <div className="age-item">
                    <span className="age-label">Day of Birth:</span>
                    <span className="age-value">{result.dayOfBirth}</span>
                  </div>
                  <div className="age-item">
                    <span className="age-label">Zodiac Sign:</span>
                    <span className="age-value">{result.zodiacSign}</span>
                  </div>
                  <div className="age-item">
                    <span className="age-label">Days Until Next Birthday:</span>
                    <span className="age-value">{result.daysUntilBirthday}</span>
                  </div>
                </div>
              </ResultBox>
            </div>
          )}
        </section>

        <section className="tool-guide">
          <div className="guide-section">
            <h2>How to Use Age Calculator</h2>
            <div className="guide-steps">
              <div className="step">
                <div className="step-number">1</div>
                <div className="step-content">
                  <h3>Enter Your Date of Birth</h3>
                  <p>Click on the date input field and select your birth date from the calendar. The calculator accepts dates in standard format and automatically validates the input to ensure accuracy.</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <h3>Automatic Calculation</h3>
                  <p>Once you select your birth date, the age calculator instantly processes the information and displays your exact age. No need to click any buttons - the calculation happens automatically as you type.</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">3</div>
                <div className="step-content">
                  <h3>Review Detailed Results</h3>
                  <p>Examine your comprehensive age breakdown including years, months, days, hours, and minutes. The calculator also provides additional information like your zodiac sign, day of birth, and countdown to your next birthday.</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">4</div>
                <div className="step-content">
                  <h3>Use Results as Needed</h3>
                  <p>Copy or note down your age information for official documents, birthday planning, or personal records. You can clear the form and calculate ages for different dates as needed.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="guide-section">
            <h2>Why Use This Tool</h2>
            <p>Our age calculator provides unparalleled accuracy and convenience for determining exact age. Unlike simple calculators that only show years, our tool breaks down your age into precise units including months, days, hours, and even minutes. This level of detail is essential for official documentation, legal requirements, medical records, and precise birthday planning.</p>
            <p>The calculator accounts for leap years and varying month lengths, ensuring mathematical precision that manual calculations often miss. Additional features like zodiac sign determination and birthday countdown add value for personal use and event planning. The tool processes everything instantly in your browser, maintaining complete privacy while delivering professional-grade results suitable for both casual and official purposes.</p>
          </div>

          <div className="guide-section">
            <h2>Frequently Asked Questions</h2>
            <div className="faq-list">
              <div className="faq-item">
                <h3>How accurate is this age calculator?</h3>
                <p>The calculator is accurate to the second. It accounts for leap years, varying month lengths, and time zones when computing your exact age.</p>
              </div>
              <div className="faq-item">
                <h3>What date format should I use?</h3>
                <p>Any standard format works — DD/MM/YYYY, MM/DD/YYYY, or YYYY-MM-DD. The calculator detects and processes the format automatically.</p>
              </div>
              <div className="faq-item">
                <h3>Does the age calculator account for leap years?</h3>
                <p>Yes. Leap years are handled automatically, including for people born on February 29. The calculation remains accurate regardless of how many leap years fall within your age range.</p>
              </div>
              <div className="faq-item">
                <h3>Can I calculate age for historical or future dates?</h3>
                <p>Yes. The calculator works for any date in the past or future — historical figures, future dates, or any custom time period — with the same accuracy as today's calculation.</p>
              </div>
              <div className="faq-item">
                <h3>Is my birth date stored anywhere?</h3>
                <p>No. All calculations run locally in your browser using JavaScript. Your birth date is never sent to a server, logged, or stored anywhere.</p>
              </div>
              <div className="faq-item">
                <h3>What zodiac signs are supported?</h3>
                <p>All 12 Western zodiac signs, from Aries through Pisces, based on your birth month and day. The sign appears automatically alongside your age results.</p>
              </div>
            </div>
          </div>

          <div className="guide-section">
            <h2>Related Age Tools</h2>
            <div className="related-tools">
              <Link to="/chronological-age-calculator/" className="related-tool">
                <div className="tool-icon">Chronological Age Calculator</div>
                <div className="tool-description">Calculate precise chronological age for medical, developmental, or psychological assessments</div>
              </Link>
              <Link to="/pet-age-calculator/" className="related-tool">
                <div className="tool-icon">Pet Age Calculator</div>
                <div className="tool-description">Convert your dog or cat's age into human years based on breed and size</div>
              </Link>
              <Link to="/generation-calculator/" className="related-tool">
                <div className="tool-icon">Generation Calculator</div>
                <div className="tool-description">Find out which generation you belong to based on your birth year</div>
              </Link>
              <Link to="/eligibility-calculator/" className="related-tool">
                <div className="tool-icon">Age Eligibility Calculator</div>
                <div className="tool-description">Check age eligibility for exams, school admissions, and milestone requirements</div>
              </Link>
              <Link to="/islamic-age-calculator/" className="related-tool">
                <div className="tool-icon">Islamic Age Calculator</div>
                <div className="tool-description">Calculate your age according to the Islamic Hijri lunar calendar</div>
              </Link>
              <Link to="/retirement-age-calculator/" className="related-tool">
                <div className="tool-icon">Retirement Age Calculator</div>
                <div className="tool-description">Find your official retirement age and eligibility date based on your birth year</div>
              </Link>
            </div>
          </div>
        </section>

        <div className="last-updated">
          <p suppressHydrationWarning>Last updated: {lastUpdated || 'Recently'}</p>
        </div>
      </div>
    </ToolPageTemplate>
  )
}

export default AgePage